import {
  type PlatformLogin,
  type PlatformRole,
  type PlatformSessionInfo,
  type PlatformSignInMethods,
  platformRoleAtLeast,
} from '@sahab/contracts'
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'

export const platformKeys = {
  all: ['platform'] as const,
  session: ['platform', 'session'] as const,
  signInMethods: ['platform', 'sign-in-methods'] as const,
}

/**
 * GET /platform/auth/me: the super-admin session (its own cookie, separate from a company's). A 401
 * means "not signed in"; the console's guard handles it, so the global expiry redirect is skipped.
 */
export const platformSessionQueryOptions = queryOptions({
  queryKey: platformKeys.session,
  queryFn: ({ signal }) =>
    api.get<PlatformSessionInfo>('/platform/auth/me', { signal, skipAuthRedirect: true }),
  staleTime: 5 * 60_000,
  refetchOnWindowFocus: false,
  retry: false,
})

/**
 * GET /platform/auth/methods: whether the sign-in page offers the password form, "Sign in with cloudTech
 * Hub" or both (ADR-042). Asked of the server, never built in, so switching needs only an API restart.
 */
export const platformSignInMethodsQueryOptions = queryOptions({
  queryKey: platformKeys.signInMethods,
  queryFn: ({ signal }) =>
    api.get<PlatformSignInMethods>('/platform/auth/methods', { signal, skipAuthRedirect: true }),
  staleTime: 60_000,
  retry: false,
})

/** The signed-in operator. Only valid under the console layout, which guarantees it is loaded. */
export function usePlatformSession(): PlatformSessionInfo {
  return useSuspenseQuery(platformSessionQueryOptions).data
}

/** UX only: whether the operator's role allows an action. The API enforces every role. */
export function usePlatformRoleAtLeast(min: PlatformRole): boolean {
  return platformRoleAtLeast(usePlatformSession().admin.role, min)
}

export function usePlatformLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PlatformLogin) =>
      api.post<PlatformSessionInfo>('/platform/auth/login', body, { skipAuthRedirect: true }),
    onSuccess: (info) => {
      // Another operator may have been signed in on this tab before: drop their console data.
      queryClient.removeQueries({ queryKey: platformKeys.all })
      queryClient.setQueryData(platformKeys.session, info)
    },
  })
}

export function usePlatformLogout() {
  return useMutation({
    mutationFn: () => api.post<void>('/platform/auth/logout', undefined, { skipAuthRedirect: true }),
  })
}

export const PLATFORM_HOME = '/platform/dashboard'
export const PLATFORM_LOGIN = '/platform/login'

/** Whether a path belongs to the super-admin console. */
export function isPlatformPath(pathname: string): boolean {
  return pathname === '/platform' || pathname.startsWith('/platform/')
}

/**
 * Where to go after signing in to the console: only same-origin console paths (never a company's
 * pages, another origin, or the console's sign-in page itself).
 */
export function safePlatformRedirect(target: string | undefined | null): string {
  if (!target?.startsWith('/') || target.startsWith('//') || target.startsWith('/\\')) return PLATFORM_HOME
  const pathname = target.split(/[?#]/, 1)[0] ?? ''
  if (!isPlatformPath(pathname)) return PLATFORM_HOME
  if (pathname === PLATFORM_LOGIN || pathname.startsWith(`${PLATFORM_LOGIN}/`)) return PLATFORM_HOME
  return target
}
